import {
  ProjectTool as ConfigProjectTool,
  configurationService,
} from '@/services/configurationService';
import { ExtendedCreateProjectData } from '@/types';
import { CheckCircle, Circle, Eye, Settings, Shield, Wrench, Zap } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ToolsStepProps {
  data: Partial<ExtendedCreateProjectData>;
  updateData: (data: Partial<ExtendedCreateProjectData>) => void;
  setValid: (isValid: boolean) => void;
}

const ToolsStep: React.FC<ToolsStepProps> = ({ data, updateData, setValid }) => {
  const { t } = useTranslation();

  // State management
  const [availableTools, setAvailableTools] = useState<ConfigProjectTool[]>([]);
  const [isLoadingTools, setIsLoadingTools] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Load tools from configuration service
  useEffect(() => {
    const loadTools = async () => {
      setIsLoadingTools(true);
      try {
        const tools = await configurationService.getProjectTools();
        setAvailableTools(tools);
      } catch (error) {
        console.error('Failed to load project tools:', error);
      } finally {
        setIsLoadingTools(false);
      }
    };

    loadTools();
  }, []);

  // Get selected tools - memoized to prevent unnecessary re-renders
  const selectedTools = useMemo(() => data.tools || [], [data.tools]);

  // Filter tools by search term
  const filteredTools = useMemo(() => {
    if (!searchTerm.trim()) return availableTools;

    const searchLower = searchTerm.toLowerCase();
    return availableTools.filter(
      tool =>
        tool.name.toLowerCase().includes(searchLower) ||
        tool.description.toLowerCase().includes(searchLower) ||
        tool.category.toLowerCase().includes(searchLower)
    );
  }, [availableTools, searchTerm]);

  // Group tools by category
  const toolsByCategory = useMemo(() => {
    const grouped: Record<string, ConfigProjectTool[]> = {};

    filteredTools.forEach(tool => {
      if (!grouped[tool.category]) {
        grouped[tool.category] = [];
      }
      grouped[tool.category].push(tool);
    });

    return grouped;
  }, [filteredTools]);

  // Category configurations
  const categories = useMemo(
    () => [
      {
        category: 'planning',
        label: t('projects.creation.tools.categories.planning'),
        description: 'Project planning and scheduling tools',
        icon: Settings,
      },
      {
        category: 'design',
        label: t('projects.creation.tools.categories.design'),
        description: 'Design and modeling tools',
        icon: Zap,
      },
      {
        category: 'construction',
        label: t('projects.creation.tools.categories.construction'),
        description: 'Construction and field management tools',
        icon: Wrench,
      },
      {
        category: 'management',
        label: t('projects.creation.tools.categories.management'),
        description: 'Project management and administration tools',
        icon: Eye,
      },
      {
        category: 'safety',
        label: t('projects.creation.tools.categories.safety'),
        description: 'Safety monitoring and reporting tools',
        icon: Shield,
      },
      {
        category: 'quality',
        label: t('projects.creation.tools.categories.quality'),
        description: 'Quality control and inspection tools',
        icon: CheckCircle,
      },
    ],
    [t]
  );

  // Validation effect
  useEffect(() => {
    // Always valid since tools are optional
    setValid(true);
  }, [setValid]);

  // Update selected tools
  const handleToolToggle = useCallback(
    (toolId: string) => {
      const currentTools = selectedTools.slice();
      const toolIndex = currentTools.indexOf(toolId);

      if (toolIndex > -1) {
        // Remove tool (only if not required)
        const tool = availableTools.find(t => t.id === toolId);
        if (!tool?.isRequired) {
          currentTools.splice(toolIndex, 1);
        }
      } else {
        // Add tool
        currentTools.push(toolId);
      }

      updateData({ tools: currentTools });
    },
    [selectedTools, availableTools, updateData]
  );

  // Select all tools in category
  const handleSelectAllInCategory = useCallback(
    (category: string) => {
      const categoryTools = toolsByCategory[category] || [];
      const currentTools = [...selectedTools];

      categoryTools.forEach(tool => {
        if (!currentTools.includes(tool.id)) {
          currentTools.push(tool.id);
        }
      });

      updateData({ tools: currentTools });
    },
    [selectedTools, toolsByCategory, updateData]
  );

  // Get required tools count
  const requiredTools = availableTools.filter(tool => tool.isRequired);

  // Add all required tools automatically
  useEffect(() => {
    const requiredToolIds = requiredTools.map(tool => tool.id);
    const currentTools = selectedTools.slice();
    let toolsAdded = false;

    requiredToolIds.forEach(toolId => {
      if (!currentTools.includes(toolId)) {
        currentTools.push(toolId);
        toolsAdded = true;
      }
    });

    if (toolsAdded) {
      updateData({ tools: currentTools });
    }
  }, [requiredTools, selectedTools, updateData]);

  return (
    <div className="space-y-6 font-['Inter',sans-serif]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        `}
      </style>

      {/* Header */}
      <div className="bg-gradient-to-br from-[var(--brand-primary-light)] to-[#FFEAB8] border border-[var(--brand-primary)] border-opacity-30 rounded-lg p-6 shadow-sm">
        <div className="flex items-start space-x-3 mb-4">
          <div className="w-10 h-10 bg-[var(--brand-primary)] rounded-lg flex items-center justify-center flex-shrink-0">
            <Settings className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
              {t('projects.creation.tools.title')}
            </h4>
            <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
              {t('projects.creation.tools.description')}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-[var(--brand-primary)] border-opacity-30 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-opacity-20 font-normal text-sm"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoadingTools ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)] mx-auto"></div>
          <p className="mt-2 text-sm text-[#6E7077]">{t('common.loading')}</p>
        </div>
      ) : (
        <>
          {/* Tools by Category */}
          {categories.map(({ category, label, description, icon: Icon }) => {
            const categoryToolsArray = toolsByCategory[category] || [];

            if (categoryToolsArray.length === 0) return null;

            const selectedInCategory = categoryToolsArray.filter(tool =>
              selectedTools.includes(tool.id)
            );

            return (
              <div
                key={category}
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-[var(--brand-primary-light)] rounded-lg flex items-center justify-center">
                      <Icon className="h-4 w-4 text-[var(--brand-primary)]" />
                    </div>
                    <div>
                      <h5 className="text-base font-bold text-[#1F1F1F] leading-[1.4em]">
                        {label}
                      </h5>
                      <p className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
                        {description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-[#6E7077] font-medium">
                      {selectedInCategory.length} / {categoryToolsArray.length}{' '}
                      {t('common.selected', 'selected')}
                    </span>
                    {selectedInCategory.length < categoryToolsArray.length && (
                      <button
                        onClick={() => handleSelectAllInCategory(category)}
                        className="text-sm font-medium text-[var(--brand-primary)] hover:text-[var(--brand-primary-hover)] transition-colors"
                      >
                        {t('projects.creation.tools.selectAll')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categoryToolsArray.map(tool => {
                    const isSelected = selectedTools.includes(tool.id);
                    const isRequired = tool.isRequired;

                    return (
                      <div
                        key={tool.id}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          isSelected
                            ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-light)] bg-opacity-30'
                            : 'border-gray-200 hover:border-[var(--brand-primary)] hover:border-opacity-30'
                        } ${isRequired ? 'bg-brand-50 border-brand-200' : ''}`}
                        onClick={() => !isRequired && handleToolToggle(tool.id)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {isSelected ? (
                              <CheckCircle className="h-5 w-5 text-[var(--brand-primary)]" />
                            ) : (
                              <Circle className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h6 className="text-sm font-bold text-[#1F1F1F] leading-[1.4em]">
                                {tool.name}
                              </h6>
                              {isRequired && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-[var(--brand-primary)] text-white uppercase tracking-wide">
                                  {t('projects.creation.tools.requiredTool')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[#6E7077] mt-1 font-normal leading-[1.75em]">
                              {tool.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Selected Tools Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h5 className="text-base font-bold text-[#1F1F1F] leading-[1.4em] mb-4">
              {t('projects.creation.tools.selectedTools')}
            </h5>

            {selectedTools.length === 0 ? (
              <div className="text-center py-6">
                <Settings className="mx-auto h-8 w-8 text-[#6E7077]" />
                <h6 className="mt-2 text-sm font-medium text-[#1F1F1F]">
                  {t('projects.creation.tools.noToolsSelected')}
                </h6>
                <p className="mt-1 text-sm text-[#6E7077] font-normal leading-[1.75em]">
                  {t('projects.creation.tools.noToolsDescription')}
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedTools.map(toolId => {
                    const tool = availableTools.find(t => t.id === toolId);
                    if (!tool) return null;

                    return (
                      <span
                        key={toolId}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          tool.isRequired
                            ? 'bg-brand-100 text-brand-800'
                            : 'bg-[var(--brand-primary-light)] text-[var(--brand-primary)]'
                        }`}
                      >
                        {tool.name}
                        {tool.isRequired && (
                          <span className="ml-1 text-xs">
                            ({t('projects.creation.tools.requiredTool')})
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
                <p className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
                  {t('projects.creation.tools.toolsSelected', { count: selectedTools.length })}
                </p>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ToolsStep;
